import * as borogove from "borogove";
import DOMPurify from "dompurify";
import Fuse from "fuse.js";
import QuillInner from "quill";
import { CircularProgress, Divider, Tabs, Tab } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Mention } from "quill-mention";
import { Quill } from "./Quill";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import {
	useNavigate,
	useLocation,
	useOutletContext,
	useAsyncValue,
} from "react-router";
import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { uuidv7 } from "uuidv7";
import {
	Button,
	AttachmentButton,
	AddUserButton,
	Avatar,
	ChatContainer,
	ConversationHeader,
	MessageGroup,
	Message,
	MessageList,
	MessageInput,
	SendButton,
	TypingIndicator,
} from "@chatscope/chat-ui-kit-react";
import {
	faCircleXmark,
	faPencil,
	faPhone,
	faVideo,
	faAnglesDown,
	faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";

import SpoolIcon from "../spool.svg?react";
import { AvatarWithPlaceholder } from "./AvatarWithPlaceholder";
import { AwaitOnce } from "./AwaitOnce";
import { MessageBubble } from "./MessageBubble";
import { MessageMenuInChat } from "./MessageMenuInChat";
import { CallMessage } from "./CallMessage";
import { CommandMenu } from "./CommandMenu";
import { CommandSession } from "./CommandSession";
import { SettingsContext } from "./Settings";
import { useEffectEvent, Attachment, ChatExtras } from "../util";

borogove.Config.relativeHashUri = true;
DOMPurify.setConfig({ ADD_TAGS: ["#document-fragment"], FORBID_TAGS: ["style", "form", "input", "select"], IN_PLACE: true });

type Group = {
	senderId: string;
	threadId: string | null;
	incoming: boolean;
	backInTime: boolean;
	type: borogove.MessageType;
	messages: borogove.ChatMessage[];
};

export const ChatInner = () => {
	const { enterIsSend, richTextToolbar, messageRequests } =
		useContext(SettingsContext);
	const navigate = useNavigate();
	const location = useLocation();
	const {
		active,
		activeChat,
		client,
		getChatExtras,
		isInvite,
		persistence,
		setChatExtras,
		typingUsers,
		wallpaper,
	} = useOutletContext<{
		active: boolean;
		activeChat: borogove.Chat;
		client: borogove.Client;
		getChatExtras: (id: string) => ChatExtras | undefined;
		isInvite: boolean;
		persistence: borogove.persistence.KeyValueStore & borogove.Persistence;
		setChatExtras: (id: string, d: Partial<ChatExtras>) => void;
		typingUsers: Array<string>;
		wallpaper: string | undefined;
	}>();

	const [commandSessions, setCommandSessions] = useState<
		Promise<borogove.CommandSession>[]
	>([]);
	const [tabNames, setTabNames] = useState<string[]>([]);
	const [tabIndex, setTabIndex] = useState(0);
	const [attachments, setAttachments] = useState<Attachment[]>([]);
	const [attachmentProgress, setAttachmentProgress] = useState(false);
	const [currentThread, setCurrentThread] = useState<string | undefined>();
	const [editing, setEditing] = useState<borogove.ChatMessage | null>(null);
	const [replyTo, setReplyTo] = useState<borogove.ChatMessage | null>(null);
	const [readUpTo, setReadUpTo] = useState<borogove.ChatMessage | null>(null);
	const [editChatName, setEditChatName] = useState(false);
	const [inputEmpty, setInputEmpty] = useState(true);
	const [virtuosoGeneration, setVirtuosoGeneration] = useState(0);
	const [theWallpaper, setTheWallpaper] = useState<string | undefined>();
	const scrollTop = useRef<string | null>(null);
	const inputRef = useRef<QuillInner>();
	const messageListContainer = useRef<HTMLDivElement | null>(null);
	const messageList = useRef<VirtuosoHandle | null>(null);

	const [messages, setMessages] = useState(
		useAsyncValue() as borogove.ChatMessage[],
	);
	const lastMessage = messages.at(-1);
	const groups: (Group | "readUpToMarker")[] = messages.reduce(
		(groups, message, idx, messages) => {
			const lastGroup = groups.at(-1);
			const penultimateGroup = groups.at(-2);
			const lastRealGroup: Group | undefined = lastGroup === "readUpToMarker" ? (penultimateGroup === "readUpToMarker" ? undefined : penultimateGroup) : lastGroup;
			const markerGroup: Array<"readUpToMarker"> =
				readUpTo && message.canReplace(readUpTo) && idx + 1 < messages.length
					? ["readUpToMarker"]
					: [];
			// There is a group less than 5 messages from the same sender and thread not more than 2 minutes long
			if (
				lastGroup &&
				lastGroup !== "readUpToMarker" &&
				lastGroup.messages.length < 5 &&
				lastGroup.senderId === message.senderId &&
				lastGroup.threadId === message.threadId &&
				lastGroup.type === message.type &&
				Math.abs(new Date(lastGroup.messages[0].timestamp).getTime() -
					new Date(message.timestamp).getTime()) <
					120000
			) {
				return [
					...groups.slice(0, -1),
					{ ...lastGroup, messages: [...lastGroup.messages, message] },
					...markerGroup,
				];
			} else {
				return [
					...groups,
					{
						senderId: message.senderId,
						threadId: message.threadId,
						incoming: message.isIncoming(),
						backInTime: !!lastRealGroup && new Date(lastRealGroup.messages[0].timestamp).getTime() > new Date(message.timestamp).getTime(),
						type: message.type,
						messages: [message],
					},
					...markerGroup,
				];
			}
		},
		[] as (Group | "readUpToMarker")[],
	);

	const messageGroupIndex = (messageId: string) => {
		for (const [idx, group] of groups.entries()) {
			if (
				group !== "readUpToMarker" &&
				group.messages.some((m) => [m.serverId, m.localId].includes(messageId))
			) {
				return idx;
			}
		}

		return null;
	};
	const hashIndex = location.hash.startsWith("#message:")
		? messageGroupIndex(location.hash.slice(9))
		: null;

	const [atBottom, setAtBottom] = useState(
		!hashIndex || groups.length - hashIndex < 2,
	);

	const isGroupChat =
		activeChat.getParticipants().length > 2 ||
		activeChat instanceof borogove.Channel;
	const invitedBy = activeChat.invitedBy();

	const clearAttachments = () => {
		for (const attachment of attachments) {
			URL.revokeObjectURL(attachment.uri);
		}
		setAttachments([]);
		setAttachmentProgress(false);
	};

	const updateInputEmpty = () => {
		const newValue = (inputRef.current?.getLength() || 0) < 2;
		if (newValue !== inputEmpty) setInputEmpty(newValue);
	};

	const onYReachStart = async () => {
		if (messages[0]) {
			const newMessages = await activeChat.getMessagesBefore(messages[0]);
			setMessages([...newMessages, ...messages]);
		}
	};

	const onYReachEnd = async () => {
		if (
			!activeChat.lastMessage ||
			!lastMessage?.canReplace(activeChat.lastMessage)
		) {
			const newMessages = await activeChat.getMessagesAfter(
				lastMessage ?? null,
			);
console.log("XYZZY onYReachEnd", activeChat.lastMessage, lastMessage, newMessages);
			if (newMessages.length < 1) return;

			setMessages([...messages, ...newMessages]);
		}
	};

	const hashChanged = useEffectEvent(async (messageId: string) => {
		if (!messageList.current) return;

		if (hashIndex) {
			messageList.current.scrollToIndex({ index: hashIndex });
		} else if (messageId) {
			const chat = activeChat;
			const message = await persistence.getMessage(
				client.accountId(),
				chat.chatId,
				messageId,
				null,
			);
			if (!message) throw "Did not find message: " + messageId;

			const got = await chat.getMessagesAround(message);
			if (got.length > 0) {
				// Loading to blank is not super helpful
				setMessages(got);
				setAtBottom(false);
				setVirtuosoGeneration((g) => g + 1);
			}
		}
	});
	useEffect(() => {
		if (location.hash.startsWith("#message:")) {
			hashChanged(location.hash.slice(9));
		}
	}, [location]);

	const newActiveConversation = useEffectEvent(async () => {
		setReadUpTo(await activeChat.readUpTo());
		setEditing(null);
		setReplyTo(null);

		const extras = getChatExtras(activeChat.chatId);
		inputRef.current?.setContents(extras?.draftDelta ?? []);
		setAttachments(extras?.attachments || []);
		setCurrentThread(extras?.currentThread);
		activeChat.setActive(true, extras?.currentThread ?? null);

		setTabIndex(extras?.tabIndex ?? 0);
		setTabNames(extras?.tabNames ?? []);
		const sessions = extras?.commandSessions ?? [];
		setCommandSessions(sessions);
		sessions.forEach((session, idx) =>
			session.then((s) =>
				setTabNames((existing) =>
					existing.map((value, i) => (i === idx ? s.name : value)),
				),
			),
		);

		setEditChatName(false);
		updateInputEmpty();
	});

	const cleanupConversation = useEffectEvent(() => {
		const draftText = (inputRef.current?.getText() || "").replace(/\s*\n/, "");
		const draftDelta = inputRef.current?.getContents();
		// scrollTo isn't really part of the user's draft but it's part of the
		// user's UI state and is needed in the same places
		// Use window.location to get any updates that we didn't tell router about
		const scrollTo =
			!atBottom && scrollTop.current ? scrollTop.current : undefined;
		setChatExtras(activeChat.chatId, {
			draftText,
			draftDelta,
			attachments,
			currentThread,
			scrollTo,
			tabIndex,
			tabNames,
			commandSessions,
		});

		activeChat.setActive(false, currentThread ?? null);
	});

	useEffect(() => {
		newActiveConversation();

		return () => {
			cleanupConversation();
		};
	}, [activeChat.chatId]);

	useEffect(() => {
		persistence
			.get(`ui#wallpaper/${activeChat?.chatId}`)
			.then((url) => setTheWallpaper(url ?? undefined));
	}, [activeChat.chatId, wallpaper, persistence]);

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
			inputRef.current.setSelection(inputRef.current.getLength());
		}
	}, [inputRef, activeChat.chatId]);

	const onlineEvent = useEffectEvent(() => {
		onYReachEnd();
	});
	useEffect(() => {
		const messageToken = client.addChatMessageListener(
			(message: borogove.ChatMessage, eventType: borogove.ChatMessageEvent) => {
				if (message.chatId() !== activeChat.chatId) return;

				setMessages((messages) => {
					const updateIdx = messages.findIndex((m) => message.canReplace(m));
					if (updateIdx > -1) {
						return [
							...messages.slice(0, updateIdx),
							message,
							...messages.slice(updateIdx + 1),
						];
					}

					if (eventType !== borogove.ChatMessageEvent.DeliveryEvent)
						return messages;

					return [...messages, message];
				});
			},
		);

		const onlineToken = client.addStatusOnlineListener(onlineEvent);

		return () => {
			client.removeEventListener(messageToken);
			client.removeEventListener(onlineToken);
		};
	}, [client, activeChat]);

	const addCommandSession = (
		name: string,
		session: Promise<borogove.CommandSession>,
	) => {
		setTabNames((existing) => existing.concat([name]));
		setCommandSessions((existing) => {
			const idx = existing.length;
			setTabIndex(idx + 1);
			session.then((s) =>
				setTabNames((existing) =>
					existing.map((value, i) => (i === idx ? s.name : value)),
				),
			);
			return existing.concat([session]);
		});
	};

	const updateCommandSession = (
		idx: number,
		session: Promise<borogove.CommandSession> | undefined,
	) => {
		if (session === undefined) {
			setTabIndex(0);
		} else {
			session.then((s) =>
				setTabNames((existing) =>
					existing.map((value, i) => (i === idx ? s.name : value)),
				),
			);
		}
		setCommandSessions((existing) =>
			existing
				.map((value, i) => (i === idx ? session : value))
				.filter((v) => !!v),
		);
	};

	const setSelectedThread = (threadId: string | null) => {
		activeChat.setActive(true, threadId);
		setCurrentThread(threadId ?? undefined);
	};

	const avatarClick = (group: Group) => {
		if (!inputRef.current) return;

		const mention = inputRef.current.getModule("mention") as Mention;
		const senderId = group.senderId;
		const displayName = activeChat.getParticipantDetails(
			group.senderId,
		)?.displayName;
		setSelectedThread(group.messages[0].threadId);

		const sel = inputRef.current.getSelection();
		const colonBefore = sel
			? inputRef.current.getText(sel.index - 2, 2).indexOf(":")
			: -1;
		if (inputRef.current.getLength() < 2) {
			mention.insertItem(
				{ denotationChar: "@", id: senderId, value: displayName },
				true,
				{ spaceAfterInsert: false },
			);
			inputRef.current.insertText(inputRef.current.getLength() - 1, ": ");
		} else if (
			sel &&
			inputRef.current.getText(sel.index, 2).indexOf(":") >= 0
		) {
			inputRef.current.insertText(sel.index, ", ");
			inputRef.current.setSelection(sel.index + 2);
			mention.insertItem(
				{ denotationChar: "@", id: senderId, value: displayName },
				true,
				{ spaceAfterInsert: false },
			);
		} else if (sel && colonBefore >= 0) {
			const start = inputRef.current.getLength() - 1 - (2 - colonBefore);
			inputRef.current.insertText(start, ", ");
			inputRef.current.setSelection(start + 2);
			mention.insertItem(
				{ denotationChar: "@", id: senderId, value: displayName },
				true,
				{ spaceAfterInsert: false },
			);
			inputRef.current.setSelection(sel.index + 3);
		} else {
			mention.insertItem(
				{ denotationChar: "@", id: senderId, value: displayName },
				true,
			);
		}
	};

	const toggleDetails = useCallback(() => {
		if (location.pathname.endsWith("/details")) {
			navigate(location.pathname.replace(/\/details$/, ""));
		} else {
			navigate("./details");
		}
	}, [location, navigate]);

	const handleChange = () => {
		activeChat.typing(
			currentThread ?? null,
			inputRef.current?.getText() ?? null,
		);
		updateInputEmpty();
	};

	const mentionHandler = (
		searchTerm: string,
		renderList: (
			matches: {
				id: string;
				value: string;
				[key: string]: string | undefined;
			}[],
		) => void,
		mentionChar: string,
	) => {
		if (!activeChat) return;

		const participants = activeChat.getParticipants();
		const possibleMentions = participants.map((p) => ({
			id: p,
			value: activeChat.getParticipantDetails(p).displayName,
		}));
		if (mentionChar === "@" && participants.length > 2) {
			renderList(
				new Fuse(possibleMentions, {
					useExtendedSearch: true,
					keys: ["value"],
				})
					.search(searchTerm)
					.map((x) => x.item),
			);
		} else {
			renderList([]);
		}
	};

	const getHTML = () => {
		if (!inputRef.current) return null;

		return inputRef.current.getSemanticHTML().replace(/&nbsp;/g, " "); // Quill replaces *every* space with nbsp...
	};

	const handleSend = () => {
		if (!activeChat) return;
		if (!inputRef.current) return;

		const isPlainText = inputRef.current
			.getContents()
			.ops.every((op) => !op.attributes && typeof op.insert !== "object");

		const body = isPlainText
			? inputRef.current.getText().replace(/\n$/, "")
			: getHTML();
		if (!body && attachments.length < 1) return;

		const message = replyTo
			? replyTo.reply()
			: new borogove.ChatMessageBuilder();
		message.threadId = currentThread || message.threadId;
		for (const { attachment } of attachments) {
			message.addAttachment(attachment);
		}
		if (body !== null && body !== undefined) {
			if (isPlainText) {
				message.text = body;
			} else {
				message.setHtml(body);
			}
		}

		if (editing) {
			activeChat.correctMessage(editing, message);
			setEditing(null);
		} else {
			activeChat.sendMessage(message);
		}

		inputRef.current?.setText("");
		clearAttachments();
		setReplyTo(null);
	};

	const attachFiles = async (files: File[]) => {
		setAttachmentProgress(true);
		await Promise.all(
			files.map((file) =>
				client.prepareAttachment(file).then((attachment) => {
					if (attachment === null) throw "Attachment failed";

					setAttachments((existing) => [
						...existing,
						{ attachment, uri: URL.createObjectURL(file) },
					]);
				}),
			),
		);
		setAttachmentProgress(false);
	};

	const handleAttach = () => {
		const input = document.createElement("input");
		input.type = "file";
		input.multiple = true;
		input.addEventListener("change", () => {
			if (!input.files || input.files.length < 1) return; // Nothing selected
			attachFiles(Array.from(input.files));
		});
		input.click();
	};

	const removeAttachment = (toRemove: Attachment) => {
		URL.revokeObjectURL(toRemove.uri);
		setAttachments(
			attachments.filter((attachment) => attachment.uri !== toRemove.uri),
		);
	};

	const editMessage = (m: borogove.ChatMessage) => {
		if (!inputRef.current) return;

		// Sanitize using DOM in-place to prevent re-parsing
		const contentFragment = new DocumentFragment();
		contentFragment.append(...m.html(borogove.Html.asDOM));
		DOMPurify.sanitize(contentFragment);
		// But Quill still wants HTML string for the dangerouslyPaste...
		const content = Array.from(contentFragment.childNodes).map(node => node instanceof Element ? node.outerHTML : node.nodeValue).join("");
		setAttachments(
			m.attachments.map((attachment) => ({
				attachment,
				uri: attachment.uris[0],
			})),
		);
		setReplyTo(m.replyToMessage);
		setCurrentThread(m.threadId ?? undefined);
		setEditing(m);
		inputRef.current.clipboard.dangerouslyPasteHTML(content);
		setTimeout(() => {
			if (!inputRef.current) return;

			inputRef.current.focus();
			inputRef.current.setSelection(inputRef.current.getLength());
		}, 100);
	};

	const setupReply = (m: borogove.ChatMessage) => {
		setReplyTo(m);
		setTimeout(() => {
			if (!inputRef.current) return;

			inputRef.current.focus();
			inputRef.current.setSelection(inputRef.current.getLength());
		}, 100);
	};

	const getTypingIndicator = () => {
		return typingUsers.map((typingUserId) => {
			// Check if typing user participates in the conversation
			const typingUserG = activeChat.getParticipantDetails(typingUserId);
			if (typingUserG) {
				return (
					<TypingIndicator
						key={`typing-${typingUserId}`}
						content={`${typingUserG.displayName} is typing`}
					/>
				);
			}

			return null;
		});
	};

	const waitForImagesLoaded = useCallback(() => {
		const content = messageListContainer.current;
		if (!content) return Promise.resolve();

		const images = content.getElementsByTagName("img");
		if (images.length === 0) return Promise.resolve();

		const promises = Array.from(images).map((img: HTMLImageElement) => {
			if (img.complete) return Promise.resolve();
			return new Promise((resolve) => {
				img.onload = resolve;
				img.onerror = resolve;
			});
		});

		return Promise.all(promises);
	}, []);

	const mostRecentMessageRef = useRef<borogove.ChatMessage | undefined>(
		undefined,
	);
	const markRead = useCallback(() => {
		if (document.hidden || !document.hasFocus()) return;
		const mostRecentMessage = mostRecentMessageRef.current;
		if (!mostRecentMessage) return;
		setTimeout(async () => {
			activeChat.markReadUpTo(mostRecentMessage);
			const tag = client.accountId() + ">" + activeChat.chatId;
			if (navigator.serviceWorker) {
				const serviceWorkerRegistration = await navigator.serviceWorker.ready;
				if (serviceWorkerRegistration.getNotifications) {
					const notifs = await serviceWorkerRegistration.getNotifications({
						tag,
					});
					notifs.forEach((notif) => notif.close());
				}
			}
		}, 0);
	}, [activeChat, client]);

	const onItemsRendered = (
		items: Array<{ data?: Group | "readUpToMarker" }>,
	) => {
		const topMessage =
			items[0]?.data === "readUpToMarker"
				? null
				: items[0]?.data?.messages?.[0];
		const topId = topMessage?.serverId || topMessage?.localId;
		if (topId) {
			// The __nuqs__ prevents nuqs from interpreting this
			history.replaceState(null, "__nuqs__", `#message:${topId}`);
			scrollTop.current = topId;
		}

		const showingMessages: borogove.ChatMessage[] = (
			items.findLast((g) =>
				g.data === "readUpToMarker"
					? {}
					: g.data?.messages?.findLast((m: borogove.ChatMessage) => m.serverId),
			)?.data as Group
		)?.messages;
		const mostRecentMessage = showingMessages
			? showingMessages.findLast((m) => m.serverId)
			: undefined;
		mostRecentMessageRef.current = mostRecentMessage;
		markRead();
	};

	useEffect(() => {
		window.addEventListener("visibilitychange", markRead);

		return () => {
			window.removeEventListener("visibilitychange", markRead);
		};
	}, [markRead]);

	const jumpToBottom = async () => {
		if (
			!activeChat.lastMessage ||
			!lastMessage?.canReplace(activeChat.lastMessage)
		) {
			// We haven't loaded all messages so we need to load not just scroll
			setMessages(await activeChat.getMessagesBefore(null));
			setAtBottom(true);
		} else {
			messageList.current?.scrollToIndex({ index: Number.MAX_SAFE_INTEGER });
		}
	};

	const onMessagesRangeChanged = (range: { endIndex: number }) => {
		setAtBottom(Number.MAX_SAFE_INTEGER - range.endIndex < 2);
	};

	useEffect(() => {
		if (atBottom) {
			(async () => {
				await waitForImagesLoaded();
				setTimeout(
					() =>
						messageList.current?.scrollToIndex({
							index: Number.MAX_SAFE_INTEGER,
						}),
					250,
				);
			})();
		}
	}, [lastMessage, atBottom, waitForImagesLoaded]);

	return (
		<ChatContainer className={active ? "active" : undefined}>
			<ConversationHeader>
				<ConversationHeader.Back onClick={() => navigate("../")} />
				<AvatarWithPlaceholder
					as={Avatar}
					photoUri={activeChat.getPhoto()}
					placeholderUri={activeChat.getPlaceholder()}
					displayName={activeChat.getDisplayName()}
					onClick={() => toggleDetails()}
					style={{ cursor: "pointer" }}
				/>
				<ConversationHeader.Content
					userName={activeChat.getDisplayName()}
					onClick={() => !editChatName && toggleDetails()}
					style={{ cursor: "pointer" }}
				>
					{!editChatName ? undefined : (
						<input
							type="text"
							defaultValue={activeChat.getDisplayName()}
							autoFocus={true}
							onBlur={() => setEditChatName(false)}
							onKeyPress={(e) => {
								if (e.key === "Enter") {
									activeChat.setDisplayName(e.currentTarget.value);
									setEditChatName(false);
								}
							}}
						/>
					)}
				</ConversationHeader.Content>
				<ConversationHeader.Actions>
					{!isInvite && (
						<Button
							title="Edit chat name"
							icon={<FontAwesomeIcon icon={faPencil} />}
							onClick={() => setEditChatName(true)}
						/>
					)}
					{!activeChat.isTrusted() && (
						<AddUserButton
							title="Trust this chat"
							onClick={() => {
								activeChat.setTrusted(true);
								activeChat.bookmark();
							}}
						/>
					)}
					{activeChat.callStatus() === borogove.calls.CallStatus.NoCall &&
						activeChat.canVideoCall() && (
							<Button
								title="Start video call"
								icon={
									<FontAwesomeIcon
										icon={faVideo}
										onClick={() => activeChat.startCall(true, true)}
									/>
								}
							/>
						)}
					{activeChat.callStatus() === borogove.calls.CallStatus.NoCall &&
						activeChat.canAudioCall() && (
							<Button
								title="Start audio call"
								icon={
									<FontAwesomeIcon
										icon={faPhone}
										onClick={() => activeChat.startCall(true, false)}
									/>
								}
							/>
						)}
					<Button
						title="Show chat details"
						icon={<FontAwesomeIcon icon={faCircleInfo} />}
						onClick={() => toggleDetails()}
					/>
				</ConversationHeader.Actions>
			</ConversationHeader>
			<div
				as={MessageList}
				className="cs-message-list"
				ref={messageListContainer}
				style={{
					background: theWallpaper
						? `url(${theWallpaper}) center / cover no-repeat`
						: "inherit",
				}}
			>
				{commandSessions.length > 0 && (
					<Tabs
						value={tabIndex}
						variant="fullWidth"
						onChange={(e, idx) => setTabIndex(idx)}
					>
						<Tab label="Chat" />
						{commandSessions.map((session, idx) => (
							<Tab key={idx} label={tabNames[idx]} />
						))}
					</Tabs>
				)}
				{tabIndex > 0 && (
					<CommandSession
						session={commandSessions[tabIndex - 1]}
						updateSession={updateCommandSession}
						idx={tabIndex - 1}
					/>
				)}
				<Virtuoso
					style={{ display: tabIndex === 0 ? "block" : "none" }}
					aria-label="Chat Messages"
					key={virtuosoGeneration}
					ref={messageList}
					followOutput={true}
					alignToBottom={true}
					startReached={onYReachStart}
					endReached={onYReachEnd}
					rangeChanged={onMessagesRangeChanged}
					itemsRendered={onItemsRendered}
					firstItemIndex={Number.MAX_SAFE_INTEGER - groups.length}
					initialTopMostItemIndex={{ index: hashIndex || "LAST" }}
					data={groups}
					itemContent={(index, g: Group | "readUpToMarker") => {
						if (g === "readUpToMarker") {
							return (
								<Divider
									key="readUpToMarker"
									variant="middle"
									textAlign="center"
								>
									New messages
								</Divider>
							);
						} else {
							const user = activeChat.getParticipantDetails(g.senderId);
							return (
								<MessageGroup className={g.backInTime ? "back-in-time" : ""} direction={g.incoming ? "incoming" : "outgoing"}>
									{(isGroupChat ||
										(g.senderId !== activeChat.chatId && g.incoming)) && (
										<AvatarWithPlaceholder
											as={Avatar}
											photoUri={user?.photoUri}
											placeholderUri={user?.placeholderUri}
											displayName={user?.displayName}
											onMouseDown={(e) => e.preventDefault()}
											onClick={() =>
												g.type !== borogove.MessageType.MessageChannelPrivate &&
												avatarClick(g)
											}
										/>
									)}
									<MessageGroup.Messages>
										{g.messages.map((m: borogove.ChatMessage, mIdx) =>
											m.type === borogove.MessageType.MessageCall ? (
												<CallMessage key={m.serverId || m.localId} m={m} />
											) : (
												<MessageBubble
													key={m.serverId || m.localId}
													chat={activeChat}
													position={
														g.messages.length === 1
															? "single"
															: mIdx >= g.messages.length - 1
																? "last"
																: mIdx < 1
																	? "first"
																	: "normal"
													}
													className={replyTo === m ? "highlight" : ""}
													client={client}
													sender={user}
													parentSender={
														m.replyToMessage &&
														activeChat.getParticipantDetails(
															m.replyToMessage.senderId,
														)
													}
													setSelectedThread={setSelectedThread}
													m={m}
												>
													{m.type !==
														borogove.MessageType.MessageChannelPrivate && (
														<MessageMenuInChat
															as={Message.Header}
															setReplyTo={setupReply}
															editMessage={editMessage}
															setSelectedThread={setSelectedThread}
															m={m}
															chat={activeChat}
														/>
													)}
												</MessageBubble>
											),
										)}
									</MessageGroup.Messages>
								</MessageGroup>
							);
						}
					}}
				/>
				{tabIndex === 0 && (
					<>
						{getTypingIndicator()}
						<Button
							title="Jump to bottom"
							className={"jump-to-bottom " + (atBottom ? "" : "visible")}
							icon={
								<FontAwesomeIcon
									icon={faAnglesDown}
									onClick={() => jumpToBottom()}
								/>
							}
						/>
					</>
				)}
				{isInvite && (
					<div className="invite-approve">
						{invitedBy && <p>Invited by {invitedBy.displayName}</p>}
						{(isGroupChat || messageRequests === "strangers") && (
							<Button
								border
								onClick={() => {
									activeChat.setTrusted(true);
									activeChat.bookmark();
								}}
							>
								{isGroupChat ? "Join" : "Approve"}
							</Button>
						)}
						{messageRequests !== "strangers" && !isGroupChat && (
							<Button border onClick={() => activeChat.bookmark()}>
								Accept
							</Button>
						)}
						{isGroupChat && activeChat.uiState === borogove.UiState.Invited && (
							<Button border onClick={() => activeChat.close()}>
								Decline
							</Button>
						)}
						<Button border onClick={() => activeChat.block()}>
							Block{" "}
							{isGroupChat &&
								activeChat.uiState === borogove.UiState.Invited &&
								"Inviter"}
						</Button>
						<Button
							border
							className="spam"
							onClick={() => activeChat.block(true, activeChat.lastMessage)}
						>
							Report SPAM
						</Button>
						<p>
							{isGroupChat
								? "Join"
								: messageRequests === "strangers"
									? "Approve"
									: "Accept"}{" "}
							to continue chatting. If blocked, the contact will not be able to
							reach you.
						</p>
					</div>
				)}
			</div>

			{tabIndex === 0 && !isInvite && (
				<div as={MessageInput} id="message-input-root">
					{replyTo && (
						<div className="reply">
							<span>
								Replying to{" "}
								<span className="h-card">
									{
										activeChat.getParticipantDetails(replyTo.senderId)
											?.displayName
									}
								</span>
							</span>
							<Button
								onClick={() => {
									setReplyTo(null);
									setSelectedThread(null);
								}}
								title="Cancel reply"
								icon={<FontAwesomeIcon icon={faCircleXmark} />}
							/>
						</div>
					)}
					{(attachmentProgress || attachments.length > 0) && (
						<div className="attachments">
							{attachments.map((attachment) => (
								<img
									crossOrigin="anonymous"
									key={attachment.uri}
									src={attachment.uri}
									alt={attachment.attachment.name ?? undefined}
									title="Click to remove"
									onClick={() => removeAttachment(attachment)}
								/>
							))}
							{attachmentProgress && <CircularProgress />}
						</div>
					)}
					<div className="cs-message-input">
						<div className="cs-message-input__tools">
							{activeChat.hasCommands() && (
								<CommandMenu chat={activeChat} addSession={addCommandSession} />
							)}
							{activeChat.canSend() && (
								<>
									<Button
										onClick={() => setSelectedThread(uuidv7())}
										title="Thread to send this message to, tap to start new"
										icon={
											currentThread ? (
												<img
													src={borogove.Identicon.svg(currentThread ?? "")}
													alt="Thread icon"
													className="svg-inline--fa"
												/>
											) : (
												<SpoolIcon />
											)
										}
									/>
									<AttachmentButton
										title="Attach media to message"
										onClick={handleAttach}
									/>
								</>
							)}
						</div>
						{activeChat.canSend() && (
							<>
								<Quill
									className="cs-message-input__content-editor-wrapper"
									ref={inputRef}
									toolbar={richTextToolbar}
									onTextChange={handleChange}
									enterIsSend={enterIsSend && handleSend}
									mentionHandler={mentionHandler}
									attachFiles={attachFiles}
									placeholder="Send message"
								/>
								{!editing && (
									<div className="cs-message-input__tools">
										<SendButton
											title="Send message"
											onClick={handleSend}
											disabled={inputEmpty && attachments.length < 1}
										/>
									</div>
								)}
							</>
						)}
					</div>
					{editing && (
						<div className="edit-tools">
							<small>editing</small>
							<Button
								border
								onClick={() => {
									setEditing(null);
									inputRef.current?.setText("");
									clearAttachments();
								}}
							>
								Cancel
							</Button>
							<Button
								border
								onClick={handleSend}
								disabled={inputEmpty && attachments.length < 1}
							>
								Save
							</Button>
						</div>
					)}
				</div>
			)}
		</ChatContainer>
	);
};

export const Chat = () => {
	const { client, activeChat, persistence } = useOutletContext<{
		client: borogove.Client;
		activeChat: borogove.Chat;
		persistence: borogove.persistence.KeyValueStore & borogove.Persistence;
	}>();

	const getMessages = async () => {
		const messageId = location.hash.startsWith("#message:")
			? location.hash.slice(9)
			: null;
		const chat = activeChat;
console.log("LOADING CHAT", chat.chatId, messageId);
		const message =
			messageId &&
			(await persistence.getMessage(
				client.accountId(),
				chat.chatId,
				messageId,
				null,
			));
		if (message) {
console.log("LOADING JUMP TO", message.timestamp, message.serverId, message);
			const messages = await chat.getMessagesAround(message);
			if (messages.length > 0) return messages;
		} else {
console.log("LOADING NO JUMP TO, LOAD END");
		}

		return chat.getMessagesBefore(null);
	};

	return (
		<AwaitOnce
			key={activeChat.chatId}
			fallback={<CircularProgress />}
			resolve={getMessages}
		>
			<ChatInner />
		</AwaitOnce>
	);
};
